import React, { useContext, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "../UserContext";
import { ThemeContext } from "../ThemeContext";
import { CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import api from '../api';
import UserPool from '../UserPool';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { startGoogleSignIn } from '../utils/cognitoOAuth';
import { clearAuthStorage } from '../authStorage';
import GoogleLogo from '../components/GoogleLogo';
import {
  resendIdentityPlatformVerification,
  signInWithGoogleIdentityPlatform,
  signInWithIdentityPlatform,
  signOutIdentityPlatform,
  usesIdentityPlatform,
} from '../identityPlatform';


// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 20px 60px ${hexToRgba(hoverColor, 0.25)}, 0 0 40px ${hexToRgba(hoverColor, 0.2)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isHovered 
          ? hexToRgba(hoverColor, 0.4)
          : isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 pointer-events-none"
        animate={{
          opacity: isHovered ? 0.2 : 0,
        }}
        transition={{ duration: 0.3 }}
        style={{
          background: `linear-gradient(135deg, ${hexToRgba(hoverColor, 0.25)}, ${hexToRgba(hoverColor, 0.4)})`,
          filter: 'blur(20px)',
          zIndex: -1,
        }}
      />
      {children}
    </motion.div>
  );
};

const LoginPage = () => {
  const { setUser } = useContext(UserContext);
  const { isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  //show password states
  const [showPassword, setShowPassword] = useState(false);

  //logging in loader state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);

  const getLoginFailureMessage = async (error) => {
    if (error.code === 'UserNotFoundException') {
      return 'Account not found.';
    }

    const maskedMissingUser =
      error.code === 'NotAuthorizedException' ||
      (error.message || '').toLowerCase().includes('incorrect username or password');

    if (!maskedMissingUser || !email.trim()) {
      return error.message || 'Login failed. Please check your credentials and try again.';
    }

    try {
      const response = await api.get(`/api/user/get_user_by_email/${encodeURIComponent(email.trim())}/`);
      if (response.data === false) {
        return 'Account not found.';
      }
    } catch (lookupError) {
      console.error('Error checking account email:', lookupError);
    }

    return error.message || 'Login failed. Please check your credentials and try again.';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      alert('Please fill in both email and password.');
      return;
    }

    setIsSubmitting(true); // Start loader

    if (usesIdentityPlatform) {
      try {
        const credential = await signInWithIdentityPlatform(email, password);
        if (!credential.user.emailVerified) {
          try {
            await resendIdentityPlatformVerification();
          } catch (error) {
            console.error('Could not resend verification email:', error);
          } finally {
            await signOutIdentityPlatform();
          }
          setStatusMessage(
            'Your email is not verified. Check your inbox for a verification link.'
          );
          setMessageType('error');
          setIsSubmitting(false);
          return;
        }

        const idToken = await credential.user.getIdToken(true);
        localStorage.setItem('idToken', idToken);
        const response = await api.post(
          '/api/user/sync_oauth_user/',
          { allow_create: true },
          { headers: { Authorization: `Bearer ${idToken}` } },
        );
        const profile = response.data;
        setUser({
          ...profile,
          user_id: profile.user_id || credential.user.uid,
          email: profile.email || credential.user.email || email,
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          screenName: profile.screen_name || credential.user.displayName || '',
        });
        const url = localStorage.getItem('redirect_url') || '/options';
        localStorage.removeItem('redirect_url');
        navigate(url);
      } catch (error) {
        clearAuthStorage();
        setUser(null);
        setStatusMessage(
          error.response?.data?.error ||
          (error.code === 'auth/invalid-credential'
            ? 'Incorrect email or password.'
            : error.message || 'Login failed. Please try again.')
        );
        setMessageType('error');
        setIsSubmitting(false);
      }
      return;
    }

    const user = new CognitoUser({ Username: email, Pool: UserPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    user.authenticateUser(authDetails, {
      onSuccess: (result) => {
        console.log('Login successful:', result);
        const idToken = result.getIdToken().getJwtToken();
        localStorage.setItem("idToken", idToken);
        const cognito_user_id = result.accessToken.payload.sub;
        const handleProfileLoadFailure = (profileError) => {
          console.error('Error getting user profile:', profileError);
          clearAuthStorage();
          setUser(null);
          setStatusMessage(
            profileError.response?.data?.detail ||
            profileError.response?.data?.error ||
            'Login succeeded, but your profile could not be loaded. Please try again.'
          );
          setMessageType('error');
          setIsSubmitting(false);
        };
        const finishLoginWithProfile = (fallbackAttributes = {}) => {
          api.get(`/api/user/get_user_profile/`).then(res => {
            console.log('res', res);
            setUser({
              ...res.data,
              user_id: res.data.user_id || cognito_user_id.replace(/-/g, ''),
              email: res.data.email || fallbackAttributes.email || email,
              firstName: res.data.first_name || fallbackAttributes['custom:FirstName'] || '',
              lastName: res.data.last_name || fallbackAttributes['custom:LastName'] || '',
              screenName: res.data.screen_name || fallbackAttributes['custom:ScreenName'] || '',
            });
            let url=localStorage.getItem('redirect_url') || "/options";
            localStorage.removeItem('redirect_url');
            navigate(url);
          }).catch(handleProfileLoadFailure);
        };

        user.getUserAttributes((err, attributes) => {
          if (err) {
            console.error('Failed to get user attributes:', err);
            finishLoginWithProfile({ email });
            return;
          }
          const attrObj = {};
          attributes.forEach(attr => {
            attrObj[attr.getName()] = attr.getValue();
          });
          finishLoginWithProfile(attrObj);
        });
      },
      onFailure: async (err) => {
        console.error('Login error:', err);
        setIsSubmitting(false);
        if (err.code === 'UserNotConfirmedException') {
          setStatusMessage('This account is not confirmed. Resending verification code...');
          setMessageType('error');
          // Delay the resend and UI update
          setTimeout(() => {
            user.resendConfirmationCode(function (err, result) {
              if (err) {
                setStatusMessage(err.message || 'Failed to resend code.');
                setMessageType('error');
                return;
              }

              setStatusMessage('Verification code sent to your email. Please enter it below.');
              setMessageType('success');
              setIsVerifying(true);
            });
          }, 2000);
        } else {
          const message = await getLoginFailureMessage(err);
          setStatusMessage(message);
          setMessageType('error');
        }
      },
    });
  };

  const handleGoogleSignIn = async () => {
    if (usesIdentityPlatform) {
      try {
        setIsGoogleSubmitting(true);
        setStatusMessage('');
        setMessageType('');
        const credential = await signInWithGoogleIdentityPlatform();
        const idToken = await credential.user.getIdToken(true);
        localStorage.setItem('idToken', idToken);
        const response = await api.post(
          '/api/user/sync_oauth_user/',
          { allow_create: false },
          { headers: { Authorization: `Bearer ${idToken}` } },
        );
        const profile = response.data;
        setUser({
          ...profile,
          user_id: profile.user_id || credential.user.uid,
          email: profile.email || credential.user.email || '',
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          screenName: profile.screen_name || credential.user.displayName || '',
        });
        const url = localStorage.getItem('redirect_url') || '/options';
        localStorage.removeItem('redirect_url');
        navigate(url);
      } catch (error) {
        await signOutIdentityPlatform().catch(() => {});
        clearAuthStorage();
        setUser(null);
        setStatusMessage(
          error.response?.data?.error ||
          error.message ||
          'Google sign-in could not be completed.'
        );
        setMessageType('error');
        setIsGoogleSubmitting(false);
      }
      return;
    }
    try {
      setIsGoogleSubmitting(true);
      setStatusMessage('');
      setMessageType('');
      await startGoogleSignIn({ allowCreate: false });
    } catch (error) {
      setStatusMessage(error.message || 'Google sign-in could not be started.');
      setMessageType('error');
      setIsGoogleSubmitting(false);
    }
  };

  const handleVerification = (e) => {
    e.preventDefault();
    const user = new CognitoUser({ Username: email, Pool: UserPool });
    user.confirmRegistration(verificationCode, true, (err, result) => {
      if (err) {
        console.error('Verification error:', err);
        setStatusMessage(err.message || 'Failed to verify code.');
        setMessageType('error');
        // Auto-clear after 4 seconds
        setTimeout(() => {
          setStatusMessage('');
          setMessageType('');
        }, 2000);        
        return;
      }
      console.log('Confirmation result:', result);
      setStatusMessage('Your account has been confirmed! You can now log in.');
      setMessageType('success');
      setIsVerifying(false);
      setVerificationCode('');
      navigate('/login');
    });
  };


  return (
    <div className="min-h-screen flex flex-col items-center justify-center font-zonaProRegular p-4 pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="w-full max-w-2xl space-y-6 sm:space-y-8">
        <Card delay={0.3} className="p-4 sm:p-6 md:p-8">
          <motion.h1 
            className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-4 sm:mb-6 text-center"
            style={{ color: isDarkMode ? '#ff7a00' : '#0a1a2f' }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {isVerifying ? 'CONFIRM YOUR ACCOUNT' : 'LOGIN'}
          </motion.h1>

          {statusMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-center p-3 sm:p-4 rounded-xl mb-3 sm:mb-4 text-sm sm:text-base ${messageType === 'success' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'}`}
            >
              {statusMessage}
            </motion.div>
          )}

          {isVerifying ? (
            <form onSubmit={handleVerification} className="space-y-4 sm:space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <input
                  type="text"
                  placeholder="Verification Code"
                  value={verificationCode}
                  onChange={(event) => setVerificationCode(event.target.value)}
                  className="w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 bg-transparent dark:text-gray-100 text-gray-900"
                  required
                />
              </motion.div>
              <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
                <motion.div 
                  className="text-left mt-4 sm:mt-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <p
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 text-xs sm:text-sm cursor-pointer transition-colors"
                  >
                    <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Back to log in
                  </p>
                </motion.div>
                <motion.div 
                  className="flex items-center justify-end"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <motion.button
                    type="submit"
                    className="py-2 sm:py-3 px-4 sm:px-8 bg-[#34c759] text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 focus:outline-none focus:ring-2 focus:ring-[#34c759] focus:ring-opacity-50 transition-all"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    CONFIRM
                  </motion.button>
                </motion.div>
              </div>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
              <motion.button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isGoogleSubmitting || isSubmitting}
                className={`w-full flex items-center justify-center gap-3 rounded-full border py-3 px-5 text-base sm:text-lg font-zonaProUltra transition-all ${
                  isGoogleSubmitting || isSubmitting
                    ? 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 dark:border-gray-600 !bg-white dark:!bg-gray-900 text-gray-800 dark:text-gray-100 hover:!bg-gray-50 dark:hover:!bg-gray-800 hover:shadow-md'
                }`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.48 }}
                whileHover={!isGoogleSubmitting && !isSubmitting ? { scale: 1.02 } : {}}
                whileTap={!isGoogleSubmitting && !isSubmitting ? { scale: 0.98 } : {}}
              >
                <GoogleLogo className="h-5 w-5 shrink-0" />
                {isGoogleSubmitting ? 'Opening Google...' : 'Continue with Google'}
              </motion.button>

              <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-400 dark:text-gray-500">
                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                <span>OR LOGIN WITH EMAIL</span>
                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
              </div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <input
                  type="text"
                  placeholder="Email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 bg-transparent dark:text-gray-100 text-gray-900"
                />
              </motion.div>
            {/* <div>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full p-4 text-xl font-zonaProRegular border-b border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500"
              />
            </div> */}
              <motion.div 
                className="mb-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="w-full p-3 sm:p-4 pr-10 sm:pr-12 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 bg-transparent dark:text-gray-100 text-gray-900"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 sm:right-3 top-[50%] -translate-y-1/2 bg-transparent p-0 m-0 border-none hover:bg-transparent focus:outline-none focus:ring-0"
                  >
                    {showPassword ? (
                      <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
                    ) : (
                      <EyeOff className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
                    )}
                  </button>
                </div>
              </motion.div>

              <motion.div 
                className="text-xs sm:text-sm text-right"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                <Link to="/forgot-password" className="font-medium font-zonaProRegular dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors" style={{ color: '#06bae4' }}>
                  Forgot Password?
                </Link>
              </motion.div>

              <motion.div 
                className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0 mt-4 sm:mt-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <div className="flex items-center space-x-2">
                  <h5 className="text-base sm:text-lg font-zonaProRegular text-gray-700 dark:text-gray-300">Don't have an account?</h5>
                  <Link to="/signup" className="font-medium dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 text-base sm:text-lg font-zonaProRegular transition-colors" style={{ color: '#06bae4' }}>
                    Sign Up
                  </Link>
                </div>
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className={`py-2 sm:py-3 px-4 sm:px-8 ${isSubmitting ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' : 'bg-[#34c759] hover:shadow-lg hover:shadow-[#34c759]/50'} text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full focus:outline-none focus:ring-2 focus:ring-[#34c759] focus:ring-opacity-50 transition-all`}
                  whileHover={!isSubmitting ? { scale: 1.05 } : {}}
                  whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                >
                  {isSubmitting ? 'Logging in...' : 'LOGIN'}
                </motion.button>
              </motion.div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
